/**
 * W1-11b (app-shell-ui): context menus from the registry's MenuIds (ARCHITECTURE.md §3.2; DESIGN.md §8.2
 * "Right-click: context menu").
 *
 * Panels plug in without code: a right-click (or the Menu key / Shift+F10) inside an element with
 * `data-context-menu="<MenuId>"`, else inside a region with a context menu (`data-region` lane, motion, curve,
 * preview, lines, timing), opens that registry menu at the pointer (at the focused element for the keyboard).
 * A panel that handles the event itself calls `preventDefault()`; text fields keep the browser's menu, and so does a
 * place whose menu is empty. Items are the menu's commands grouped by `MenuPlacement.group` (separators between
 * groups), with their shortcuts and disabled reasons (UiBridge). Commands chosen from it run with source 'context'
 * and the right click as `ctx.event` (a PointerEvent with the click position), so "at the mouse" commands act where
 * the menu was opened. `menuItemsFor` builds the same items for the shell's own dropdowns (project menu, overflow).
 */

import * as DM from '@radix-ui/react-dropdown-menu';
import { useEffect, useMemo } from 'react';
import type { Json } from '../../core/util/types';
import { UiBridgeProvider, useUiBridge } from '../../ui/bridge';
import { MenuItems, menuStyles, type MenuKit } from '../../ui/internal/menuItems';
import type { MenuItem, UiBridge } from '../../ui/types';
import type { AppServices, CommandId, CommandSource, MenuId, Registry } from '../registry/types';
import type { ShellUiService, TriggerEvent, UiSnapshot } from '../uiService';
import { isTextInput } from './KeyboardRouter';
import { useOptionalShell, useText, useUi } from './ShellContext';
import { m } from './shell.messages';
import s from './Shell.module.css';

const KIT: MenuKit = {
  Item: DM.Item,
  CheckboxItem: DM.CheckboxItem,
  ItemIndicator: DM.ItemIndicator,
  Separator: DM.Separator,
  Sub: DM.Sub,
  SubTrigger: DM.SubTrigger,
  SubContent: DM.SubContent,
  Portal: DM.Portal,
};

const MENU_IDS: readonly MenuId[] = [
  'app.project',
  'app.edit',
  'app.view',
  'app.help',
  'lane.context',
  'motion.context',
  'curve.context',
  'preview.context',
  'lines.context',
  'inspector.toolbar',
  'library.card',
  'timing.context',
  'tools.menu',
];

/** Regions whose right-click opens a registry menu. */
export const REGION_MENUS: Readonly<Record<string, MenuId>> = {
  lane: 'lane.context',
  motion: 'motion.context',
  curve: 'curve.context',
  preview: 'preview.context',
  lines: 'lines.context',
  timing: 'timing.context',
};

export function isMenuId(v: string): v is MenuId {
  return (MENU_IDS as readonly string[]).includes(v);
}

/** The registry menu for a right-clicked element, or null. */
export function menuFor(target: Element | null): MenuId | null {
  if (target === null) return null;
  const explicit = target.closest('[data-context-menu]')?.getAttribute('data-context-menu') ?? null;
  if (explicit !== null) return isMenuId(explicit) ? explicit : null;
  const region = target.closest('[data-region]')?.getAttribute('data-region') ?? null;
  return region === null ? null : (REGION_MENUS[region] ?? null);
}

/** A registry menu as menu items: its groups in order, separated. */
export function menuItemsFor(registry: Registry, id: MenuId): MenuItem[] {
  const out: MenuItem[] = [];
  for (const group of registry.menu(id)) {
    if (group.commands.length === 0) continue;
    if (out.length > 0) out.push({ kind: 'separator' });
    for (const cmd of group.commands) out.push({ kind: 'command', command: cmd.id });
  }
  return out;
}

const selectMenu = (snap: UiSnapshot): UiSnapshot['contextMenu'] => snap.contextMenu;

/**
 * The right click as a command trigger event (CommandContext.event is a Keyboard-, Pointer- or WheelEvent): browsers
 * that fire `contextmenu` as a PointerEvent pass it as is; otherwise an equivalent PointerEvent carries the position,
 * buttons, modifiers and target.
 */
export function contextTriggerEvent(e: MouseEvent): TriggerEvent | null {
  if (typeof PointerEvent === 'function' && e instanceof PointerEvent) return e;
  if (typeof PointerEvent !== 'function') return null;
  const ev = new PointerEvent('contextmenu', {
    clientX: e.clientX,
    clientY: e.clientY,
    screenX: e.screenX,
    screenY: e.screenY,
    button: e.button,
    buttons: e.buttons,
    ctrlKey: e.ctrlKey,
    shiftKey: e.shiftKey,
    altKey: e.altKey,
    metaKey: e.metaKey,
    pointerType: 'mouse',
    isPrimary: true,
  });
  Object.defineProperty(ev, 'target', { value: e.target, enumerable: true });
  return ev;
}

/** The registry's run with a trigger event (AppRegistry.runAny; a registry without it ignores the extra argument). */
type RunWithEvent = (
  id: CommandId,
  args?: Json,
  source?: CommandSource,
  event?: TriggerEvent,
  at?: number,
) => Promise<boolean>;

/** The app's UiBridge with runs reported as context-menu use, carrying the event that opened the menu. */
function useContextBridge(services: AppServices, event: TriggerEvent | null, at: number | null): UiBridge {
  const base = useUiBridge();
  return useMemo(
    () => ({
      ...base,
      run: (command: string, args?: Json): void => {
        const registry = services.registry as Registry & { readonly runAny: RunWithEvent };
        void registry.runAny(command, args, 'context', event ?? undefined, at ?? undefined);
      },
    }),
    [base, services, event, at],
  );
}

export function ContextMenuHost(props: { readonly services: AppServices; readonly ui: ShellUiService }) {
  const { services, ui } = props;
  const menu = useUi(ui, selectMenu);
  const text = useText(services);
  const focus = useOptionalShell()?.focus ?? null;

  useEffect(() => {
    const onContextMenu = (e: MouseEvent): void => {
      if (e.defaultPrevented) return;
      const target = e.target instanceof Element ? e.target : null;
      if (target === null || isTextInput(target)) return;
      const id = menuFor(target);
      if (id === null || menuItemsFor(services.registry, id).length === 0) return;
      e.preventDefault();
      let x = e.clientX;
      let y = e.clientY;
      if (x === 0 && y === 0) {
        // Keyboard (Menu key, Shift+F10): open at the focused element.
        const r = target.getBoundingClientRect();
        x = r.left + Math.min(24, r.width / 2);
        y = r.top + Math.min(24, r.height / 2);
      }
      ui.openContextMenu(id, x, y, contextTriggerEvent(e));
    };
    document.addEventListener('contextmenu', onContextMenu);
    return () => {
      document.removeEventListener('contextmenu', onContextMenu);
    };
  }, [services, ui]);

  if (menu === null) return null;
  return <OpenContextMenu services={services} ui={ui} menu={menu} label={text.t(m.contextMenuLabel)} focus={focus} />;
}

function OpenContextMenu(props: {
  readonly services: AppServices;
  readonly ui: ShellUiService;
  readonly menu: NonNullable<UiSnapshot['contextMenu']>;
  readonly label: string;
  readonly focus: { handBack(): boolean } | null;
}) {
  const { services, ui, menu, focus } = props;
  const bridge = useContextBridge(services, menu.event, menu.at ?? null);
  const items = menuItemsFor(services.registry, menu.menu);
  return (
    <DM.Root
      key={menu.nonce}
      open
      onOpenChange={(open) => {
        if (!open) ui.closeContextMenu();
      }}
    >
      <DM.Trigger asChild>
        <span className={s.contextAnchor} style={{ left: menu.x, top: menu.y }} aria-hidden="true" />
      </DM.Trigger>
      <DM.Portal>
        <DM.Content
          className={menuStyles.content}
          side="bottom"
          align="start"
          sideOffset={2}
          collisionPadding={8}
          loop
          aria-label={props.label}
          onCloseAutoFocus={(e) => {
            // The anchor is not focusable: give focus back to the region the menu was opened in, unless what the
            // chosen command opened (a popover, a field) has taken it.
            e.preventDefault();
            const active = document.activeElement;
            if (active === null || active === document.body || active === document.documentElement) focus?.handBack();
          }}
        >
          <UiBridgeProvider bridge={bridge}>
            <MenuItems kit={KIT} items={items} />
          </UiBridgeProvider>
        </DM.Content>
      </DM.Portal>
    </DM.Root>
  );
}
